import { DateTime } from 'luxon'
import { autoCapitalizeName } from '@/utils/autoCapitalizeName'
import { ProfessionalsBodyParams, Profile } from '../scheduled-types'

export class ProfessionalsMapper {
  toDomain(professional: Profile) {
    return {
      professionalFormatted: {
        id: professional?.professional?.id,
        name: autoCapitalizeName(professional?.professional?.name),
        specialty: professional?.professional?.specialtyName,
        council: ProfessionalsMapper.formatProfessionalCouncil(
          professional?.professional,
        ),
        photo: professional?.professional?.urlPhoto,
        about: professional?.professional?.graduation ?? '-',
        councilType: professional?.professional?.councilType,
        duration: professional?.professional?.appointmentDuration,
        price: professional?.professional?.appointmentPrice,
        reviews: {
          average: professional?.review?.rating,
          total: professional?.review?.total,
          label: 'avaliações',
        },
        workingFields:
          professional?.professional?.themes?.map(theme => ({
            label: theme,
          })) || [],
        appointments: {
          total: professional?.professional?.totalAppointments,
          label: 'atendimentos',
        },
      },
      ...professional,
    }
  }

  toPersistence(data: ProfessionalsBodyParams) {
    const payload = {
      idPatient: data?.idPatient,
      genericFilter: data?.term,
      themeIds: data.reasons?.map(reason => reason.key),
      appointmentDuration: data.durations?.map(duration => duration.key),
      ethnicityIds: data.ethnicities?.map(ethnicity => ethnicity.key),
      specialtyIds: data.specialties?.map(specialty => specialty.key),
      genders: data.genders?.map(gender => gender.key),
      state: data.state?.value,
      city: data.city?.value,
      librasAssistance: data?.providesSignLanguageService,
      availabilityStartDate: ProfessionalsMapper.formatJSDateToApiPattern(
        data?.availabilityStart,
      ),
      availabilityEndDate: ProfessionalsMapper.formatJSDateToApiPattern(
        data?.availabilityEnd,
      ),
      approachIds: data.approaches?.map(approach => approach.key),
      page: data?.page ?? 1,
      executeCount: false,
      limit: 2,
      professionalType: data?.professionalType,
      ...(data?.professionalId && { professionalId: data?.professionalId }),
      ...(data?.specialtyId && { specialtyId: data?.specialtyId }),
      ...(data?.professionalType === 'PSICOLOGO' && {
        isPsychologistAlreadyMigrated: true,
      }),
    }

    const isValid = value =>
      value !== undefined &&
      value !== null &&
      !(Array.isArray(value) && value?.length === 0) &&
      !(typeof value === 'string' && value.trim() === '')

    return Object.keys(payload)?.reduce((acc, key) => {
      if (isValid(payload[key])) {
        acc[key] = payload[key]
      }
      return acc
    }, {})
  }

  private static formatProfessionalCouncil(
    professional: Profile['professional'],
  ) {
    if (!professional?.councilType) return ''

    if (professional?.councilUf) {
      return `${professional?.councilType} ${
        professional?.councilNumer || ''
      } (${professional?.councilUf})`
    }

    return `${professional?.councilType} ${professional?.councilNumer || ''}`
  }

  private static formatJSDateToApiPattern(date: Date) {
    if (!date) return ''
    return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd')
  }
}

export default new ProfessionalsMapper()
